import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {CalendarCheck2, LockKeyhole, Mail, MessageSquareText, ShieldCheck} from 'lucide-react';
import {useLayoutEffect, useMemo, useState} from 'react';
import {apiRequest} from '../api';
import {Badge, Modal, PrimaryButton, SecondaryButton} from '../design';
import {LoadingPanel, formatDateTime} from './PagePrimitives';

type CalendarEvent = {
  summary: string;
  description: string;
  start: {dateTime: string; timeZone?: string};
  end: {dateTime: string; timeZone?: string};
  visibility: 'private';
};

type CalendarProposal = {
  id: string;
  version: number;
  state: string;
  operation?: 'create' | 'update' | 'delete';
  opportunityId?: string;
  sourceConversationId?: string;
  sourceEmailRef?: string;
  sourceRefs?: string[];
  summary?: string;
  description?: string;
  start?: CalendarEvent['start'];
  end?: CalendarEvent['end'];
  event?: CalendarEvent;
  providerEventId?: string;
  providerEtag?: string;
  updatedAt: string;
  sourceContext?: {
    conversationSummary: string;
    conversationOccurredAt: string;
    recruiterEmails: string[];
    message?: {
      subject: string;
      from: string;
      occurredAt: string;
    };
  };
};

type EventDraft = {
  summary: string;
  description: string;
  start: string;
  end: string;
  timeZone: string;
};

type FieldDiff = {field: string; before: string | null; after: string | null};

const localInputValue = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
};

const defaultDraft = (proposal?: CalendarProposal): EventDraft => {
  const source = proposal?.event ?? proposal;
  const startFallback = new Date();
  startFallback.setDate(startFallback.getDate() + 1);
  startFallback.setMinutes(0, 0, 0);
  const endFallback = new Date(startFallback.getTime() + 60 * 60_000);
  return {
    summary: source?.summary ?? '',
    description: source?.description ?? '',
    start: localInputValue(source?.start?.dateTime ?? startFallback.toISOString()),
    end: localInputValue(source?.end?.dateTime ?? endFallback.toISOString()),
    timeZone:
      source?.start?.timeZone ??
      source?.end?.timeZone ??
      Intl.DateTimeFormat().resolvedOptions().timeZone ??
      'UTC',
  };
};

const eventFromDraft = (draft: EventDraft): CalendarEvent => ({
  summary: draft.summary.trim(),
  description: draft.description,
  start: {dateTime: new Date(draft.start).toISOString(), timeZone: draft.timeZone.trim() || 'UTC'},
  end: {dateTime: new Date(draft.end).toISOString(), timeZone: draft.timeZone.trim() || 'UTC'},
  visibility: 'private',
});

const readableValue = (field: keyof EventDraft, value: string): string =>
  field === 'start' || field === 'end'
    ? value ? formatDateTime(new Date(value).toISOString()) : ''
    : value;

const diffDraft = (before: EventDraft, after: EventDraft): FieldDiff[] =>
  (Object.keys(after) as Array<keyof EventDraft>)
    .filter((field) => before[field] !== after[field])
    .map((field) => ({
      field: field === 'timeZone' ? 'Time zone' : field.charAt(0).toUpperCase() + field.slice(1),
      before: readableValue(field, before[field]) || null,
      after: readableValue(field, after[field]) || null,
    }));

export const CalendarProposalReview = ({
  proposalId,
  opened,
  onClose,
}: {
  proposalId: string | null;
  opened: boolean;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const proposal = useQuery({
    queryKey: ['calendar-proposal', proposalId],
    queryFn: () => apiRequest<CalendarProposal>('/calendar/proposals/' + encodeURIComponent(proposalId!)),
    enabled: opened && Boolean(proposalId),
  });
  const [draft, setDraft] = useState<EventDraft>(() => defaultDraft());
  const [reviewedVersion, setReviewedVersion] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (proposal.data) {
      setDraft(defaultDraft(proposal.data));
      setReviewedVersion(null);
    }
  }, [proposal.data]);

  const baseline = useMemo(() => defaultDraft(proposal.data), [proposal.data]);
  const fieldDiffs = useMemo(() => diffDraft(baseline, draft), [baseline, draft]);
  const deleting = proposal.data?.operation === 'delete';
  const updating = proposal.data?.operation === 'update';
  const displayedDiffs: FieldDiff[] = deleting
    ? [{field: 'Event', before: proposal.data?.summary ?? 'Private event', after: 'Removed'}]
    : fieldDiffs;
  const valid =
    deleting ||
    Boolean(
      draft.summary.trim() &&
      draft.start &&
      draft.end &&
      draft.timeZone.trim() &&
      Date.parse(draft.start) < Date.parse(draft.end) &&
      (!updating || fieldDiffs.length > 0),
    );

  const save = useMutation({
    mutationFn: async () => {
      if (!proposal.data) throw new Error('The calendar proposal is not loaded.');
      return apiRequest<CalendarProposal>('/calendar/proposals/' + encodeURIComponent(proposal.data.id), {
        method: 'PATCH',
        body: JSON.stringify({
          expectedVersion: proposal.data.version,
          operation: proposal.data.operation ?? 'create',
          ...(deleting ? {} : {event: eventFromDraft(draft)}),
          ...(proposal.data.sourceConversationId ? {sourceConversationId: proposal.data.sourceConversationId} : {}),
          ...(proposal.data.sourceEmailRef ? {sourceEmailRef: proposal.data.sourceEmailRef} : {}),
          ...(proposal.data.providerEventId ? {providerEventId: proposal.data.providerEventId} : {}),
          ...(proposal.data.providerEtag ? {providerEtag: proposal.data.providerEtag} : {}),
          fieldDiffs: displayedDiffs,
        }),
      });
    },
    onSuccess: (updated) => {
      setReviewedVersion(updated.version);
      void queryClient.invalidateQueries({queryKey: ['calendar-proposals']});
    },
  });

  const approve = useMutation({
    mutationFn: () => {
      if (!proposal.data || reviewedVersion === null) throw new Error('Save the reviewed fields first.');
      return apiRequest<CalendarProposal>(
        '/calendar/proposals/' + encodeURIComponent(proposal.data.id) + '/approve',
        {
          method: 'POST',
          body: JSON.stringify({approved: true, expectedVersion: reviewedVersion}),
        },
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['action-items']}),
        queryClient.invalidateQueries({queryKey: ['calendar-proposals']}),
        queryClient.invalidateQueries({queryKey: ['bootstrap']}),
      ]);
      onClose();
    },
  });

  const updateDraft = (field: keyof EventDraft, value: string) => {
    setDraft((current) => ({...current, [field]: value}));
    setReviewedVersion(null);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Review private calendar change"
      centered
      size="xl"
    >
      {proposal.isLoading ? <LoadingPanel label="Loading calendar proposal" /> : null}
      {proposal.isError ? (
        <p className="pd-form-error" role="alert">
          {proposal.error instanceof Error ? proposal.error.message : 'The proposal could not be loaded.'}
        </p>
      ) : null}
      {proposal.data ? (
        <div className="pd-calendar-review">
          <div className="pd-review-banner">
            <ShieldCheck size={20} aria-hidden="true" />
            <div>
              <strong>Candidate-only calendar event</strong>
              <p>Nothing is sent to recruiters. Invitations and attendees are structurally excluded.</p>
            </div>
            <Badge leftSection={<LockKeyhole size={12} aria-hidden="true" />}>Private · no attendees</Badge>
          </div>

          <section className="pd-source-reference" aria-labelledby="calendar-source-title">
            <div>
              <span className="pd-eyebrow">Source material</span>
              <h3 id="calendar-source-title">Review where this proposal came from.</h3>
            </div>
            {proposal.data.sourceContext ? (
              <div>
                <p>
                  <MessageSquareText size={16} aria-hidden="true" />
                  {proposal.data.sourceContext.conversationSummary}
                </p>
                {proposal.data.sourceContext.recruiterEmails.length ? (
                  <p>Recruiter: {proposal.data.sourceContext.recruiterEmails.join(', ')}</p>
                ) : null}
                <p>Conversation activity {formatDateTime(proposal.data.sourceContext.conversationOccurredAt)}</p>
              </div>
            ) : proposal.data.sourceConversationId ? (
              <p>
                <MessageSquareText size={16} aria-hidden="true" />
                Source conversation is linked, but its summary is no longer available.
              </p>
            ) : null}
            {proposal.data.sourceContext?.message ? (
              <p>
                <Mail size={16} aria-hidden="true" />
                {proposal.data.sourceContext.message.subject} · From {proposal.data.sourceContext.message.from || 'unknown sender'} · {formatDateTime(proposal.data.sourceContext.message.occurredAt)}
              </p>
            ) : proposal.data.sourceEmailRef ? <p>The exact source email is linked but no longer available.</p> : null}
            {proposal.data.sourceConversationId || proposal.data.sourceEmailRef ? (
              <details open>
                <summary>Audit source references</summary>
                {proposal.data.sourceConversationId ? (
                  <p>Conversation <code>{proposal.data.sourceConversationId}</code></p>
                ) : null}
                {proposal.data.sourceEmailRef ? (
                  <p>Gmail message <code>{proposal.data.sourceEmailRef}</code></p>
                ) : null}
              </details>
            ) : null}
            {!proposal.data.sourceConversationId && !proposal.data.sourceEmailRef ? (
              <p>Created from opportunity {proposal.data.opportunityId ?? 'context'}; no email source is attached.</p>
            ) : null}
          </section>

          {!deleting ? (
            <fieldset className="pd-calendar-fields" disabled={save.isPending || approve.isPending}>
              <legend>Editable private event</legend>
              <label>
                <span>Summary</span>
                <input
                  required
                  value={draft.summary}
                  onChange={(event) => updateDraft('summary', event.currentTarget.value)}
                />
              </label>
              <label className="pd-calendar-field-wide">
                <span>Description</span>
                <textarea
                  rows={5}
                  value={draft.description}
                  onChange={(event) => updateDraft('description', event.currentTarget.value)}
                />
              </label>
              <label>
                <span>Starts</span>
                <input
                  type="datetime-local"
                  required
                  value={draft.start}
                  onChange={(event) => updateDraft('start', event.currentTarget.value)}
                />
              </label>
              <label>
                <span>Ends</span>
                <input
                  type="datetime-local"
                  required
                  value={draft.end}
                  onChange={(event) => updateDraft('end', event.currentTarget.value)}
                />
              </label>
              <label className="pd-calendar-field-wide">
                <span>Time zone</span>
                <input
                  required
                  value={draft.timeZone}
                  onChange={(event) => updateDraft('timeZone', event.currentTarget.value)}
                  placeholder="America/New_York"
                />
              </label>
            </fieldset>
          ) : (
            <div className="pd-permission-note">
              <CalendarCheck2 size={18} aria-hidden="true" />
              <p>This proposal cancels a private PursuitDeck-owned event. The provider event and ETag are revalidated before deletion.</p>
            </div>
          )}

          <section className="pd-calendar-diff" aria-labelledby="calendar-diff-title">
            <div className="pd-note-heading">
              <div>
                <span className="pd-eyebrow">Field-level diff</span>
                <h3 id="calendar-diff-title">{displayedDiffs.length} changed field{displayedDiffs.length === 1 ? '' : 's'}</h3>
              </div>
              <span>Expected version {proposal.data.version}</span>
            </div>
            {displayedDiffs.length ? (
              <dl>
                {displayedDiffs.map((difference) => (
                  <div key={difference.field}>
                    <dt>{difference.field}</dt>
                    <dd>
                      <span><small>Before</small>{difference.before ?? 'Not set'}</span>
                      <span><small>After</small>{difference.after ?? 'Cleared'}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="pd-empty-copy">No field values changed. Saving still records that you reviewed this exact version.</p>
            )}
          </section>

          {!valid ? (
            <p className="pd-form-error" role="alert">
              {updating && fieldDiffs.length === 0
                ? 'Change at least one field before reviewing an event update.'
                : 'Add a summary, valid start and end times, and a time zone. The end must follow the start.'}
            </p>
          ) : null}
          {save.isError || approve.isError ? (
            <p className="pd-form-error" role="alert">
              {(save.error ?? approve.error) instanceof Error
                ? (save.error ?? approve.error as Error).message
                : 'The calendar proposal could not be saved.'}
            </p>
          ) : null}

          <div className="pd-calendar-review-actions">
            <SecondaryButton type="button" onClick={onClose}>Close</SecondaryButton>
            <SecondaryButton
              type="button"
              loading={save.isPending}
              disabled={!valid}
              onClick={() => save.mutate()}
            >
              Save reviewed fields
            </SecondaryButton>
            <PrimaryButton
              type="button"
              loading={approve.isPending}
              disabled={reviewedVersion === null || save.isPending}
              onClick={() => approve.mutate()}
            >
              {deleting
                ? 'Approve private cancellation'
                : updating
                  ? 'Approve private event update'
                  : 'Approve and add private event'}
            </PrimaryButton>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};
